import React, { useEffect, useRef, useState } from 'react';
import classNames from 'classnames';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation } from 'swiper/modules';
import { SwiperOptions } from 'swiper/types';
import type { Swiper as SwiperType } from 'swiper';
import 'swiper/css';
import 'swiper/css/navigation';
import { v4 as uuidv4 } from 'uuid';
import IconButton, {
  IconButtonTypes,
} from '@lib/components/IconButton/IconButton';
import styles from './Carousel.module.scss';

type Props = {
  children: React.ReactNode[];
  withWrap?: boolean;
  spaceBetween?: number;
  slidesPerView?: number | 'auto';
  breakpoints?: SwiperOptions['breakpoints'];
  slideWidth?: number | 'auto';
  navigationButtonType?: IconButtonTypes;
  hideNavigationAtEdges?: boolean;
  leftNavClassName?: string;
  rightNavClassName?: string;
};

export default function Carousel(props: Props) {
  const {
    children,
    withWrap,
    spaceBetween,
    slidesPerView = 1,
    breakpoints,
    slideWidth = 144,
    navigationButtonType = IconButtonTypes.PrimaryFilled,
    hideNavigationAtEdges = false,
    leftNavClassName,
    rightNavClassName,
  } = props;
  const navigationPrevRef = useRef(null);
  const navigationNextRef = useRef(null);
  const [swiperInstance, setSwiperInstance] = useState<SwiperType | null>(null);
  const [isBeginning, setIsBeginning] = useState(true);
  const [isEnd, setIsEnd] = useState(false);

  useEffect(() => {
    if (
      swiperInstance?.params?.navigation &&
      typeof swiperInstance.params.navigation === 'object'
    ) {
      swiperInstance.params.navigation.prevEl = navigationPrevRef.current;
      swiperInstance.params.navigation.nextEl = navigationNextRef.current;
      swiperInstance.navigation.init();
      swiperInstance.navigation.update();
    }
  }, [swiperInstance, children]);

  const handleSlideChange = (swiper: SwiperType) => {
    setIsBeginning(swiper.isBeginning);
    setIsEnd(swiper.isEnd);
  };

  const handleSwiper = (swiper: SwiperType) => {
    setSwiperInstance(swiper);
    setIsBeginning(swiper.isBeginning);
    setIsEnd(swiper.isEnd);
  };

  const hidePrevButton = hideNavigationAtEdges && isBeginning;
  const hideNextButton = hideNavigationAtEdges && isEnd;

  return (
    <div
      className={classNames(styles.carousel, {
        [styles.carouselWithWrap]: withWrap,
      })}
    >
      <Swiper
        onSwiper={handleSwiper}
        onSlideChange={handleSlideChange}
        onReachBeginning={() => setIsBeginning(true)}
        onReachEnd={() => setIsEnd(true)}
        onFromEdge={() => {
          setIsBeginning(swiperInstance?.isBeginning ?? false);
          setIsEnd(swiperInstance?.isEnd ?? false);
        }}
        className={uuidv4()}
        modules={[Navigation]}
        spaceBetween={spaceBetween}
        slidesPerView={slidesPerView}
        breakpoints={breakpoints}
        navigation={{
          prevEl: navigationPrevRef.current,
          nextEl: navigationNextRef.current,
        }}
        pagination={{ clickable: true }}
        autoplay={{ delay: 1000 }}
        loop={false}
      >
        {children.map((child) => (
          <SwiperSlide
            key={uuidv4()}
            style={slideWidth === 'auto' ? undefined : { width: slideWidth }}
          >
            {child}
          </SwiperSlide>
        ))}
      </Swiper>
      <div
        className={classNames(styles.leftNavWrap, leftNavClassName, {
          [styles.hidden]: hidePrevButton,
        })}
      >
        <IconButton
          ref={navigationPrevRef}
          icon="chevron_left"
          rounded
          type={navigationButtonType}
        />
      </div>
      <div
        className={classNames(styles.rightNavWrap, rightNavClassName, {
          [styles.hidden]: hideNextButton,
        })}
      >
        <IconButton
          ref={navigationNextRef}
          icon="chevron_right"
          rounded
          type={navigationButtonType}
        />
      </div>
    </div>
  );
}
